import React from 'react';
import {
  RocketLaunchIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import ToolCallout from '../../../components/playbooks/ToolCallout';

const CultureOffsiteStage3 = () => {
  return (
    <div className="max-w-none text-gray-300">
      <PlaybookSection
        title="Stage 3: Publish & Implement"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="h1"
      >
        {/* TL;DR section */}
        <div className="mb-10">
          <h2 className="text-2xl font-semibold text-white mb-4">TL;DR</h2>
          
          <ExpandableCallout 
            title="What do I need to DO in this stage?" 
            type="info"
            defaultOpen={true}
          >
            <p className="mb-4">You need to do 3 things:</p>
            <div className="space-y-4 mb-4">
              <div className="flex items-start">
                <span className="text-primary-light font-bold mr-3">1</span>
                <p>Synthesize the table groups' work into a single map</p>
              </div>
              <div className="flex items-start">
                <span className="text-primary-light font-bold mr-3">2</span>
                <p>Publish the learnings/insights and invite a dot vote</p>
              </div>
              <div className="flex items-start">
                <span className="text-primary-light font-bold mr-3">3</span>
                <p>Review your people roadmap and communicate that you've done that as a result of this session</p>
              </div>
            </div>
            <p>Here's what we mean...</p>
          </ExpandableCallout>
        </div>

        {/* Analysis and Synthesis Section */}
        <PlaybookSection
          title="Analysis and Synthesis"
          icon={<DocumentTextIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <p className="text-gray-300 mb-6">
              OK not gonna lie. This takes some time, but it's worth it! 😅
            </p>
            
            <p className="text-gray-300 mb-6">
              The goal here is to:
            </p>
            
            <div className="mb-6">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center bg-primary/10 rounded-full mr-3">
                  <span className="text-primary-light">🎯</span>
                </div>
                <p>consolidate the table group's work into a single picture that expresses how you work</p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center bg-primary/10 rounded-full mr-3">
                  <span className="text-primary-light">🎯</span>
                </div>
                <p>stack rank the priorities for things that folks want to change (good data that informs your people roadmap)</p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6">
              You can do this any way you like, I've found the following structure works best and it's the same principles whether you're working from a Miro board or from physical stickies and whiteboard.
            </p>
            
            <ol className="space-y-4 mb-6 pl-6">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-3">1.</span>
                <p>Collate all table groups' maps onto 1 x giant board (physical or Miro depending on how you ran this) of your culture today 'how you currently work'</p>
              </li>
              
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-3">2.</span>
                <p>Then remove duplicates or merge and rewrite similar ideas</p>
              </li>
              
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-3">3.</span>
                <p>Collate all table groups' priorities for improvement. Use duplicates as a way to create a stack rank priority list of focus areas tagged by the element of culture it relates to (e.g. 'how we give feedback' or 'how we celebrate success')</p>
              </li>
            </ol>
            
            <p className="text-gray-300 mb-6">
              Your final output should look something like this:
            </p>
            
            <div className="space-y-8 mb-8">
              <div>
                <h4 className="text-lg font-medium text-white mb-4">1. Culture Map</h4>
                <PlaybookImage
                  src="https://auth.openorg.fyi/storage/v1/object/public/resources/culture-offsite/culture-map.png"
                  alt="Culture Map Example"
                  className="rounded-lg border border-gray-700 mb-4"
                />
                
                <p className="text-gray-300 text-sm">
                  Sometimes it's helpful to color code / heat-map the stickies based on what teams thought we should start(amber), stop (red) or continue(green)
                </p>
                
                <PlaybookImage
                  src="https://auth.openorg.fyi/storage/v1/object/public/resources/culture-offsite/heat-map.png"
                  alt="Heat Map Example"
                  className="rounded-lg border border-gray-700 mt-4"
                />
              </div>
              
              <div>
                <h4 className="text-lg font-medium text-white mb-4">2. Priorities for improvement</h4>
                <PlaybookImage
                  src="https://auth.openorg.fyi/storage/v1/object/public/resources/culture-offsite/priorities.png"
                  alt="Priorities Example"
                  className="rounded-lg border border-gray-700"
                />
              </div>
            </div>
          </PlaybookCard>
        </PlaybookSection>

        {/* Publish Learnings Section */}
        <PlaybookSection
          title="Publish the learnings/insights and your 'final' board"
          icon={<DocumentDuplicateIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <h3 className="text-lg font-semibold text-white mb-4">Action: share the output with the wider org</h3>
            
            <CopyToNotion
              title="Findings Communication Template"
              slack={true}
            >
              <div className="p-4 bg-gray-800 rounded-lg">
                <p className="mb-4">
                  Hey folks,
                </p>
                
                <p className="mb-4">
                  Thanks to everyone for contributing to our culture workshop on the offsite. I've collated the findings into a single map that expresses how we work and identified the areas we want to improve on.
                </p>
                
                <p className="mb-4">
                  The current stack rank of priorities I've put together is based on how many teams mentioned it as an area we should focus on improving.
                </p>
                
                <p className="mb-4">
                  I'll be using the output to inform what the People & Culture team work on over the next quarter so would appreciate your feedback.
                </p>
                
                <p className="mb-4">
                  Thanks
                </p>
                
                <p>
                  - your name
                </p>
              </div>
            </CopyToNotion>
          </PlaybookCard>
        </PlaybookSection>

        {/* Reprioritize Roadmap Section */}
        <PlaybookSection
          title="Re-prioritize your people roadmap and publish it"
          icon={<ChartBarIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <p className="text-gray-300 mb-6">
              Looking at the data you've just gathered, it's a great time to refresh your people roadmap taking into account the priorities raised by the teams.
            </p>
            
            <p className="text-gray-300 mb-6">
              Once you've refactored:
            </p>
            
            <div className="flex items-start mb-8">
              <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center bg-primary/10 rounded-full mr-3">
                <span className="text-primary-light">🚀</span>
              </div>
              <p>Publish + Explain your reprioritization decision</p>
            </div>
            
            <h3 className="text-lg font-semibold text-white mb-4">Outcomes:</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>You brought all voices into the culture conversation and got shared understanding and alignment on 'how we work today' and 'how we want to work tomorrow'</p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>You made culture tangible</p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>You crowd-sourced opportunities for improvement and made sure every voice was heard</p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>You were radically transparent</p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>You converted workshop conversation → into action and reprioritized your people roadmap 'out loud' to create an accountability loop</p>
              </div>
            </div>
            
            <ExpandableCallout
              title="Pro Tip"
              type="tip"
              className="mt-6"
            >
              <p className="text-gray-300">
                This story of what you did and how you did it, is a great blog post in the making.
              </p>
            </ExpandableCallout>
          </PlaybookCard>
        </PlaybookSection>
      </PlaybookSection>
    </div>
  );
};

export default CultureOffsiteStage3; 